/**
 * Tax › a business client's return workpaper: the year-end trial balance from QuickBooks grouped
 * into the lines of Form 1120-S, 1065 or 1120 by description, with the checks, the balance sheet,
 * every account's line (which staff can change), and saving to the Workpapers folder.
 */
import { formatCents, type Workpaper } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileDown } from "lucide-react";
import { useState } from "react";
import { Empty, ListSearch, Loading, matches, Pager, StatRow, usePaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { defaultTaxYear, formatDate } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

type Account = { accountId: string; account: string; debit: number; credit: number; concept: string | null; type: string | null; chosen: boolean; line: string | null };
type Data = { year: number; form: "1120S" | "1065" | "1120"; formName: string; start: string; end: string; asOf: string; accounts: Account[]; workpaper: Workpaper; lines: Array<{ key: string; label: string; section: string }> };

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const money = (c: number) => formatCents(c);
const dollars = (c: number) => formatCents(c, { showCents: false });
const SECTIONS: Record<string, string> = { income: "Income", deduction: "Deductions", asset: "Assets", liability: "Liabilities", equity: "Equity" };

function Lines({ rows }: { rows: Array<{ label: string; amount: number; bold?: boolean }> }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.label}${i}`} className={cn("border-b border-border last:border-0", r.bold && "font-semibold")}>
            <td className="py-1.5 pr-3">{r.label}</td>
            <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Check({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span aria-hidden className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", ok ? "bg-emerald-500" : "bg-red-500")} />
      <span>
        <span className="font-medium">{label}</span> <span className="text-muted-foreground">{detail}</span>
      </span>
    </li>
  );
}

export function BusinessWorkpaper({ clientId, me }: { clientId: string; me: Me }) {
  const [year, setYear] = useState(defaultTaxYear());
  const [form, setForm] = useState<"" | "1120S" | "1065" | "1120">("");
  const [q, setQ] = useState("");
  const qs = new URLSearchParams({ year: String(year), ...(form ? { form } : {}) });
  const { data, isLoading, error, isFetching } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/tax/workpapers?${qs}`], retry: false, placeholderData: (prev) => prev });
  const canEdit = hasPermission(me, "engagements.write");
  const { toast } = useToast();
  const setLine = useMutation({ meta: { inlineError: true }, mutationFn: (x: { accountId: string; concept: string | null }) => api.send("PUT", `/api/clients/${clientId}/tax/workpapers/accounts/${x.accountId}`, { concept: x.concept }) });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ documents: Array<{ filename: string }> }>("POST", `/api/clients/${clientId}/tax/workpapers/save`, { year, form: data!.form }),
    onSuccess: () => toast({ title: "Workpaper saved", description: `The PDF and CSV are in the client's ${year} Workpapers folder.` }),
  });
  const accounts = (data?.accounts ?? []).filter((a) => matches(q, a.account, a.line ?? ""));
  const paged = usePaged(accounts, { resetKey: `${q}${year}${form}` });
  const years = Array.from({ length: 4 }, (_, i) => defaultTaxYear() - i);
  const header = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <select aria-label="Tax year" className={select} value={year} onChange={(e) => setYear(Number(e.target.value))}>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <select aria-label="Return" className={select} value={form || data?.form || ""} onChange={(e) => setForm(e.target.value as typeof form)}>
        {!data && !form && <option value="">Choose the return</option>}
        <option value="1120S">Form 1120-S</option>
        <option value="1065">Form 1065</option>
        <option value="1120">Form 1120</option>
      </select>
      {data && (
        <span className="text-xs text-muted-foreground">
          {formatDate(data.start)} to {formatDate(data.end)} · QuickBooks as of {formatDate(data.asOf)}
          {isFetching && " · updating…"}
        </span>
      )}
      {data && canEdit && (
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => save.mutate()} disabled={save.isPending}>
          <FileDown className="h-3.5 w-3.5" /> {save.isPending ? "Saving…" : "Save PDF and CSV to Workpapers"}
        </Button>
      )}
    </div>
  );
  // A page of the trial balance grouped into the return's lines (shorter when QuickBooks isn't connected).
  if (isLoading) return <Loading size="list" />;
  if (!data)
    return (
      <>
        {header}
        <Empty>{errorMessage(error)}</Empty>
      </>
    );
  const w = data.workpaper;
  const ordinaryLabel = data.form === "1120" ? "Taxable income before special deductions" : "Ordinary business income (loss)";
  const err = setLine.error ?? save.error;
  return (
    <div>
      {header}
      {err && <p className="mb-3 text-sm text-destructive">{errorMessage(err)}</p>}
      <StatRow
        stats={[
          { label: "Total income", value: dollars(w.income.total) },
          { label: "Total deductions", value: dollars(w.deductions.total) },
          { label: ordinaryLabel, value: dollars(w.ordinaryIncome) },
          { label: "Book net income", value: dollars(w.m1.bookNetIncome) },
        ]}
      />
      <ul className="mt-4 space-y-1.5 rounded-md border border-border p-3">
        <Check ok={w.balanced} label="Trial balance" detail={w.balanced ? `balances: ${money(w.debits)} of debits and credits.` : `does not balance: debits ${money(w.debits)}, credits ${money(w.credits)}.`} />
        <Check ok={w.m1.difference === 0} label="Book income to the return (M-1)" detail={w.m1.difference === 0 ? `ties: ${money(w.m1.bookNetIncome)} of book income${w.m1.mealsNotDeducted ? `, plus ${money(w.m1.mealsNotDeducted)} of meals not deducted` : ""}${w.m1.penalties ? `, plus ${money(w.m1.penalties)} of penalties` : ""}${w.m1.federalIncomeTax ? `, plus ${money(w.m1.federalIncomeTax)} of federal income tax` : ""}.` : `is off by ${money(w.m1.difference)}: an income or expense account is grouped on a balance sheet line, or the other way round.`} />
        <Check ok={w.m2.difference === 0} label={data.form === "1065" ? "Partners' capital (M-2)" : "Retained earnings (M-2)"} detail={w.m2.difference === 0 ? `rolls forward: ${money(w.m2.beginning)} + ${money(w.m2.netIncome)} − ${money(w.m2.distributions)} of distributions = ${money(w.m2.end)}.` : `is off by ${money(w.m2.difference)}: entries were posted straight to equity, or earlier distributions were closed into retained earnings this year.`} />
        <Check ok={w.totals.assets[1] === w.totals.liabilitiesAndEquity[1]} label="Balance sheet" detail={`end of year: assets ${money(w.totals.assets[1])}, liabilities and equity ${money(w.totals.liabilitiesAndEquity[1])}.`} />
        {w.warnings.map((x) => (
          <li key={x} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
            <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
            {x}
          </li>
        ))}
      </ul>

      <div className="mt-4 grid gap-4 2xl:grid-cols-2">
        <div className="min-w-0 rounded-md border border-border p-3">
          <h3 className="mb-1 text-sm font-semibold">Income and deductions</h3>
          <Lines
            rows={[
              ...w.income.lines.map((l) => ({ label: l.label, amount: l.amount })),
              { label: "Gross profit", amount: w.income.grossProfit, bold: true },
              { label: "Total income", amount: w.income.total, bold: true },
              ...w.deductions.lines.map((l) => ({ label: l.label, amount: l.amount })),
              { label: "Total deductions", amount: w.deductions.total, bold: true },
              { label: ordinaryLabel, amount: w.ordinaryIncome, bold: true },
            ]}
          />
          {w.separatelyStated.length > 0 && (
            <>
              <h3 className="mb-1 mt-3 text-sm font-semibold">Stated separately to owners</h3>
              <Lines rows={w.separatelyStated.map((l) => ({ label: l.label, amount: l.amount }))} />
            </>
          )}
        </div>
        {/* min-w-0: the grid cell stays the screen's width, so the table scrolls inside its box on a phone. */}
        <div className="min-w-0 rounded-md border border-border p-3">
          <h3 className="mb-1 text-sm font-semibold">Balance sheet</h3>
          <TableScroll label="Balance sheet table">
          <table className="w-full min-w-[26rem] text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1.5 text-left font-medium">
                  <span className="sr-only">Line</span>
                </th>
                <th className="whitespace-nowrap py-1.5 pl-4 text-right font-medium">Beginning of year</th>
                <th className="whitespace-nowrap py-1.5 pl-4 text-right font-medium">End of year</th>
              </tr>
            </thead>
            <tbody>
              {w.scheduleL.map((l) => (
                <tr key={l.concept} className="border-b border-border">
                  <td className="py-1.5 pr-3">{l.label}</td>
                  <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(l.beginning)}</td>
                  <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(l.end)}</td>
                </tr>
              ))}
              <tr className="border-b border-border font-semibold">
                <td className="py-1.5 pr-3">Total assets</td>
                <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(w.totals.assets[0])}</td>
                <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(w.totals.assets[1])}</td>
              </tr>
              <tr className="font-semibold">
                <td className="py-1.5 pr-3">Total liabilities and equity</td>
                <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(w.totals.liabilitiesAndEquity[0])}</td>
                <td className="tabular whitespace-nowrap py-1.5 pl-4 text-right">{money(w.totals.liabilitiesAndEquity[1])}</td>
              </tr>
            </tbody>
          </table>
          </TableScroll>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Accounts and their lines</h3>
          <ListSearch value={q} onChange={setQ} placeholder="Search accounts or lines" />
        </div>
        <p className="mb-2 text-xs text-muted-foreground">Each account starts on a line chosen from its QuickBooks type, detail type and name. A line you choose is kept for this client in later years. Line numbers are not shown; use the descriptions on that year's form.</p>
        <TableScroll label="Accounts and their lines table" className="rounded-md border border-border">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-secondary/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Account</th>
                <th className="px-3 py-2 text-right font-medium">Debit</th>
                <th className="px-3 py-2 text-right font-medium">Credit</th>
                <th className="px-3 py-2 text-left font-medium">Line</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map((a) => (
                <tr key={a.accountId} className="border-t border-border">
                  <td className="px-3 py-1.5">
                    {a.account}
                    {a.type && <span className="block text-xs text-muted-foreground">{a.type}</span>}
                  </td>
                  <td className="tabular px-3 py-1.5 text-right">{a.debit ? money(a.debit) : ""}</td>
                  <td className="tabular px-3 py-1.5 text-right">{a.credit ? money(a.credit) : ""}</td>
                  <td className="px-3 py-1.5">
                    {canEdit ? (
                      <select aria-label={`Line for ${a.account}`} className={cn(select, "h-8 w-full max-w-xs", !a.concept && "border-red-400")} value={a.concept ?? ""} onChange={(e) => setLine.mutate({ accountId: a.accountId, concept: e.target.value || null })} disabled={setLine.isPending}>
                        {!a.concept && <option value="">Choose a line</option>}
                        {Object.entries(SECTIONS).map(([sec, label]) => (
                          <optgroup key={sec} label={label}>
                            {data.lines
                              .filter((l) => l.section === sec)
                              .map((l) => (
                                <option key={l.key} value={l.key}>
                                  {l.label}
                                </option>
                              ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      (a.line ?? "Not assigned")
                    )}
                    {a.chosen && canEdit && (
                      <button type="button" className="ml-2 text-xs text-muted-foreground underline" onClick={() => setLine.mutate({ accountId: a.accountId, concept: null })}>
                        Use the default
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <Pager paged={paged} noun="accounts" className="mt-3" />
      </div>
    </div>
  );
}
